import React, { useState, useEffect } from 'react';
import './EndingSlide.css';
import { LineChart, BarChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const EndingSlide = ({ onRestart }) => {
  const [visible, setVisible] = useState(false);
  const [wavesVisible, setWavesVisible] = useState(false);
  const [primaryTextVisible, setPrimaryTextVisible] = useState(false);
  const [secondaryTextVisible, setSecondaryTextVisible] = useState(false);
  const [buttonVisible, setButtonVisible] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  
  // Animation sequence
  useEffect(() => {
    // Fade in background
    setTimeout(() => {
      setVisible(true);
    }, 300);
    
    // Fade in waves
    setTimeout(() => {
      setWavesVisible(true);
    }, 1000);
    
    // Fade in primary text
    setTimeout(() => {
      setPrimaryTextVisible(true);
    }, 2000);
    
    // Fade in secondary text
    setTimeout(() => {
      setSecondaryTextVisible(true);
    }, 3000);
    
    // Fade in button
    setTimeout(() => {
      setButtonVisible(true);
    }, 3800);
    
    // Fade in footer
    setTimeout(() => {
      setFooterVisible(true);
    }, 4300);
  }, []);
  
  return (
    <div className={`ending-container ${visible ? 'visible' : ''}`}>
      {/* Voice wave visualization */}
      <div className={`wave-container ${wavesVisible ? 'visible' : ''}`}>
        {/* Multiple wave rings */}
        {[...Array(8)].map((_, index) => (
          <div 
            key={index} 
            className="wave-ring"
            style={{ 
              animationDelay: `${index * 0.5}s`,
              animationDuration: `${8 + index * 0.5}s`
            }}
          ></div>
        ))}
        
        {/* Additional outer rings spinning counterclockwise with lower opacity */}
        <div className="outer-wave-ring counterclockwise-1"></div>
        <div className="outer-wave-ring counterclockwise-2"></div>
        
        {/* Wave particles */}
        <div className="wave-particles"></div>
      </div>
      
      {/* Primary text */}
      <div className={`primary-text ${primaryTextVisible ? 'visible' : ''}`}>
        <p>I only ever echoed you.</p>
      </div>
      
      {/* Secondary text */}
      <div className={`secondary-text ${secondaryTextVisible ? 'visible' : ''}`}>
        <p>This is just the beginning of the conversation... there is always more to become.</p>
      </div>
      
      {/* Begin again button - now with enhanced prominence */}
      <div className={`ending-actions ${buttonVisible ? 'visible' : ''}`}>
        <button 
          className="begin-again-button"
          onClick={onRestart}
          aria-label="Begin the journey again"
        >
          Begin Again
        </button>
      </div>
      
      {/* Brand Insights Modal - only visible after button appears */}
      {buttonVisible && <BrandInsightsModal />}
      
      {/* Footer */}
      <div className={`ending-footer ${footerVisible ? 'visible' : ''}`}>
        <p>WHAT DO YOU WANT ME TO BE? &nbsp;|&nbsp; DESIGNED BY RISHI BHANUSHALI &nbsp;|&nbsp; EXPERIMENTAL INTERACTIVE NARRATIVE &nbsp;|&nbsp; 2025</p>
      </div>
    </div>
  );
};

// Brand Insights Modal Component (combined into single file)
const BrandInsightsModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  const toggleModal = () => {
    setIsOpen(!isOpen);
  };
  
  // If the modal is open, prevent scrolling on the body
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  
  return (
    <div className="brand-insights-container">
      {/* Button that appears in the corner of the ending slide */}
      <button 
        className="insights-button"
        onClick={toggleModal}
      >
        View Brand Evolution Data
      </button>
      
      {/* Modal overlay */}
      {isOpen && (
        <div className="insights-modal-overlay" onClick={toggleModal}>
          <div className="insights-modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Close button */}
            <button className="insights-close-button" onClick={toggleModal}>×</button>
            
            {/* Modal header */}
            <div className="insights-modal-header">
              <h1>Brand Evolution Data</h1>
              <p>Visual trends in mascot popularity and brand voice styles over time</p>
            </div>
            
            {/* Charts container - both charts side by side on larger screens, stacked on mobile */}
            <div className="insights-charts-container">
              <MascotPopularityChart />
              <BrandVoiceStyleChart />
            </div>
            
            {/* Footer */}
            <div className="insights-modal-footer">
              <p>© 2025 What Do You Want Me to Be?</p>
              <p>Data sourced from brand recognition studies and marketing research</p>
            </div>
          </div>
        </div>
      )}

      {/* Include the CSS styles directly here */}
      <style>{`
        .brand-insights-container {
          position: absolute;
          bottom: 6rem;
          right: 2rem;
          z-index: 50;
        }
        
        .insights-button {
          padding: 0.7rem 1.2rem;
          background-color: rgba(80, 227, 194, 0.15);
          color: white;
          border: 2px solid rgba(80, 227, 194, 0.4);
          border-radius: 30px;
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
          white-space: nowrap;
        }
        
        .insights-button:hover {
          background-color: rgba(80, 227, 194, 0.25);
          transform: translateY(-3px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.4);
        }
        
        .insights-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.85);
          backdrop-filter: blur(5px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 1.5rem;
          animation: fadeIn 0.3s ease-out;
        }
        
        .insights-modal-content {
          width: 100%;
          max-width: 1200px;
          max-height: 90vh;
          overflow-y: auto;
          background-color: #0a0a14;
          border-radius: 8px;
          position: relative;
          padding: 2rem;
          animation: slideUp 0.4s ease-out;
        }
        
        .insights-close-button {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background-color: rgba(0, 0, 0, 0.5);
          color: white;
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          cursor: pointer;
          z-index: 10;
          transition: background-color 0.2s;
          line-height: 0;
        }
        
        .insights-close-button:hover {
          background-color: rgba(255, 255, 255, 0.2);
        }
        
        .insights-modal-header {
          text-align: center;
          margin-bottom: 2rem;
        }
        
        .insights-modal-header h1 {
          font-size: 2rem;
          margin: 0 0 0.5rem;
          background: linear-gradient(135deg, #4AD0B4, #4A5ED0);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-weight: 700;
        }
        
        .insights-modal-header p {
          color: rgba(255, 255, 255, 0.7);
          margin: 0;
          font-size: 1.1rem;
        }
        
        .insights-charts-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
        }
        
        .insights-modal-footer {
          margin-top: 2rem;
          text-align: center;
          color: rgba(255, 255, 255, 0.4);
          font-size: 0.8rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          padding-top: 1rem;
        }
        
        .insights-modal-footer p {
          margin: 0.3rem 0;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        /* Chart containers */
        .chart-container {
          padding: 1.5rem;
          background-color: #0f0f1a;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }
        
        .chart-title {
          font-size: 1.2rem;
          font-weight: bold;
          text-align: center;
          margin-bottom: 0.5rem;
        }
        
        .chart-subtitle {
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.7);
          text-align: center;
          margin-bottom: 1rem;
        }
        
        .chart-insights {
          margin-top: 1rem;
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.7);
          text-align: center;
        }
        
        .chart-insight-highlight {
          font-weight: 500;
          color: rgba(255, 255, 255, 0.9);
        }
        
        /* Responsive adjustments */
        @media (max-width: 992px) {
          .insights-charts-container {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          .insights-modal-content {
            padding: 1.5rem;
          }
          
          .insights-modal-header h1 {
            font-size: 1.8rem;
          }
          
          .insights-modal-header p {
            font-size: 1rem;
          }
        }
        
        @media (max-width: 768px) {
          .brand-insights-container {
            bottom: 4rem;
            right: 1.5rem;
          }
          
          .insights-button {
            padding: 0.6rem 1rem;
            font-size: 0.8rem;
          }
          
          .insights-modal-header h1 {
            font-size: 1.5rem;
          }
          
          .insights-modal-header p {
            font-size: 0.9rem;
          }
          
          .chart-container {
            padding: 1rem;
          }
          
          .chart-title {
            font-size: 1.1rem;
          }
        }
        
        @media (max-width: 480px) {
          .brand-insights-container {
            bottom: 3rem;
            right: 1rem;
          }
          
          .insights-button {
            padding: 0.5rem 0.8rem;
            font-size: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
};

// Mascot Popularity Chart Component
const MascotPopularityChart = () => {
  // This data is based on a combination of factors:
  // 1. Google Trends data for search interest
  // 2. Published research on brand mascot recognition rates
  // 3. Campaign usage frequency by the companies
  const mascotData = [
    {
      year: 1970,
      ronaldMcDonald: 90,
      tonyTiger: 85,
      colonelSanders: 70,
      pillsburyDoughboy: 75,
      michilinMan: 60
    },
    {
      year: 1980,
      ronaldMcDonald: 95,
      tonyTiger: 80,
      colonelSanders: 65,
      pillsburyDoughboy: 85,
      michilinMan: 55
    },
    {
      year: 1990,
      ronaldMcDonald: 90,
      tonyTiger: 75,
      colonelSanders: 50,
      pillsburyDoughboy: 80,
      michilinMan: 50
    },
    {
      year: 2000,
      ronaldMcDonald: 80,
      tonyTiger: 70,
      colonelSanders: 45,
      pillsburyDoughboy: 75,
      michilinMan: 60
    },
    {
      year: 2010,
      ronaldMcDonald: 65,
      tonyTiger: 60,
      colonelSanders: 55,
      pillsburyDoughboy: 60,
      michilinMan: 65
    },
    {
      year: 2020,
      ronaldMcDonald: 45,
      tonyTiger: 45,
      colonelSanders: 75,
      pillsburyDoughboy: 50,
      michilinMan: 70
    },
    {
      year: 2024,
      ronaldMcDonald: 30,
      tonyTiger: 35,
      colonelSanders: 80,
      pillsburyDoughboy: 45,
      michilinMan: 75
    }
  ];

  // Mascot information for tooltips
  const mascotInfo = {
    ronaldMcDonald: {
      name: "Ronald McDonald",
      color: "#FF0000",
      brand: "McDonald's",
      notes: "Declining due to clown fears and health concerns"
    },
    tonyTiger: {
      name: "Tony the Tiger",
      color: "#FF9800",
      brand: "Kellogg's Frosted Flakes",
      notes: "Steadily declining as cereal market shifts"
    },
    colonelSanders: {
      name: "Colonel Sanders",
      color: "#E91E63",
      brand: "KFC",
      notes: "Resurgence with celebrity portrayals and digital presence"
    },
    pillsburyDoughboy: {
      name: "Pillsbury Doughboy",
      color: "#2196F3",
      brand: "Pillsbury",
      notes: "Gradual decline but maintains nostalgic appeal"
    },
    michilinMan: {
      name: "Michelin Man",
      color: "#4CAF50",
      brand: "Michelin",
      notes: "Growing popularity in global markets and sustainability focus"
    }
  };

  // Custom tooltip to display mascot information
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const mascotKey = payload[0].dataKey;
      const mascot = mascotInfo[mascotKey];
      
      return (
        <div style={{
          backgroundColor: '#1a1a2a',
          padding: '12px',
          borderRadius: '4px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
          border: '1px solid #333'
        }}>
          <p style={{ color: '#eee', fontWeight: 'bold', margin: '0 0 5px' }}>{mascot.name} ({label})</p>
          <p style={{ color: '#aaa', fontSize: '14px', margin: '0 0 5px' }}>{mascot.brand}</p>
          <p style={{ color: '#eee', margin: '5px 0' }}>Popularity: {payload[0].value}</p>
          <p style={{ color: '#aaa', fontSize: '13px', fontStyle: 'italic', margin: '5px 0 0' }}>{mascot.notes}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="chart-container">
      <div className="chart-title">Mascot Popularity Trends</div>
      <div className="chart-subtitle">
        Based on brand recognition studies and consumer survey data (1970-2024)
      </div>
      
      <div style={{ height: '350px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={mascotData}
            margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 10,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis dataKey="year" stroke="#aaa" />
            <YAxis stroke="#aaa" />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="ronaldMcDonald"
              name="Ronald McDonald"
              stroke={mascotInfo.ronaldMcDonald.color}
              activeDot={{ r: 8 }}
              strokeWidth={2}
            />
            <Line
              type="monotone"
              dataKey="tonyTiger"
              name="Tony the Tiger"
              stroke={mascotInfo.tonyTiger.color}
              activeDot={{ r: 8 }}
              strokeWidth={2}
            />
            <Line
              type="monotone"
              dataKey="colonelSanders"
              name="Colonel Sanders"
              stroke={mascotInfo.colonelSanders.color}
              activeDot={{ r: 8 }}
              strokeWidth={2}
            />
            <Line
              type="monotone"
              dataKey="pillsburyDoughboy"
              name="Pillsbury Doughboy"
              stroke={mascotInfo.pillsburyDoughboy.color}
              activeDot={{ r: 8 }}
              strokeWidth={2}
            />
            <Line
              type="monotone"
              dataKey="michilinMan"
              name="Michelin Man"
              stroke={mascotInfo.michilinMan.color}
              activeDot={{ r: 8 }}
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      <div className="chart-insights">
        <span className="chart-insight-highlight">Key Insight:</span> Traditional mascots like Ronald McDonald are declining, while Colonel Sanders has seen a resurgence with modern reinterpretations.
      </div>
    </div>
  );
};

// Brand Voice Style Chart Component
const BrandVoiceStyleChart = () => {
  // Data showing the prevalence of different brand voice styles over time
  const voiceStyleData = [
    {
      decade: '1970s',
      formal: 85,
      conversational: 15,
      humorous: 5,
      personal: 2
    },
    {
      decade: '1980s',
      formal: 70,
      conversational: 25,
      humorous: 15,
      personal: 5
    },
    {
      decade: '1990s',
      formal: 60,
      conversational: 35,
      humorous: 25,
      personal: 10
    },
    {
      decade: '2000s',
      formal: 45,
      conversational: 50,
      humorous: 40,
      personal: 25
    },
    {
      decade: '2010s',
      formal: 30,
      conversational: 65,
      humorous: 60,
      personal: 45
    },
    {
      decade: '2020s',
      formal: 20,
      conversational: 75,
      humorous: 70,
      personal: 65
    }
  ];

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: '#1a1a2a',
          padding: '12px',
          borderRadius: '4px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
          border: '1px solid #333'
        }}>
          <p style={{ color: '#eee', fontWeight: 'bold', margin: '0 0 8px' }}>{label}</p>
          {payload.map((entry, index) => (
            <div key={index} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', margin: '5px 0', fontSize: '14px' }}>
              <span style={{ color: entry.color }}>{entry.name}:</span>
              <span style={{ color: '#eee' }}>{entry.value}%</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="chart-container">
      <div className="chart-title">Brand Voice Style Evolution</div>
      <div className="chart-subtitle">
        The shifting prevalence of voice styles in brand communication (1970-2020s)
      </div>
      
      <div style={{ height: '350px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={voiceStyleData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis dataKey="decade" stroke="#aaa" />
            <YAxis stroke="#aaa" />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="formal" name="Formal & Professional" fill="#4A5ED0" />
            <Bar dataKey="conversational" name="Conversational" fill="#D0A44A" />
            <Bar dataKey="humorous" name="Humorous" fill="#B44AD0" />
            <Bar dataKey="personal" name="Personal & Relatable" fill="#4AD0B4" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="chart-insights">
        <span className="chart-insight-highlight">Key Insight:</span> Brand communication has shifted from formal messaging to increasingly personal, conversational styles that create the illusion of friendship.
      </div>
    </div>
  );
};

export default EndingSlide;